import format from 'date-fns/format'
import { memo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import s from './AdSpotInfoModal.module.scss'

import { STATUS_NAME_BY_STATUS_VALUE, STATUS_STYLE_BY_STATUS_NUMBER } from '../AdSpotCard'
import { SURFACE_TYPE_BY_VALUE } from '../CreateAdSpotModal/components/Step1'

import { getIconByMetaverse } from 'constants/MetaverseIcons'

import { Intervals } from 'utils/createIntervalFromString'
import transformUTCToLocale from 'utils/transformUTCToLocale'

import { useFetch } from 'hooks/useFetch'
import { useShowDropdown } from 'hooks/useShowDropdown'

import { adSpotsApi } from 'api/adSpots-api'

import { LockIcon } from 'assets/icons/LockIcon'

import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { Dropmenu } from 'components/UI/Dropmenu/Dropmenu'
import { ModalFullScreen } from 'components/UI/ModalFullScreen/ModalFullScreen'
import { Status } from 'components/UI/Status/Status'
import CustomTooltip from 'components/UI/Tooltip'

import { createAdSpotStatus } from 'pages/MyAdSpots/helpers/createAdSpotStatus'

const AdSpotInfoModal = memo(() => {
  const [searchParams] = useSearchParams()
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const [period, setPeriod] = useState<Intervals>('year')
  const { ref, showDropdown } = useShowDropdown()

  const { data: adSpot, loading } = useFetch({
    getData: () =>
      location.pathname.includes('publisher') ? adSpotsApi.getOne(+(id || 0)) : adSpotsApi.getPublicOne(+(id || 0)),
  })
  const { data: statistics } = useFetch({
    getData: () =>
      location.pathname.includes('marketplace')
        ? adSpotsApi.getPublicStats(id ? +id : 0, period || 'year')
        : adSpotsApi.getStats(id ? +id : 0, period || 'year'),
    deps: [id, period.toLowerCase()],
  })
  const { data: pin, loading: pinLoading } = useFetch({
    getData: () => adSpotsApi.getPin(+(id || 0)),
    deps: [id],
    fetchIf: !!(id && location.pathname.includes('my-adspaces')),
  })
  const { data: modType, loading: modTypeLoading } = useFetch({
    getData: () => adSpotsApi.getModType(+(id || 0)),
    fetchIf: !!(id && location.pathname.includes('my-adspaces')),
  })

  const changeIntervalHandler = (period: Intervals) => {
    setPeriod(period)
  }

  const onCloseModal = () => {
    navigate(`../?${searchParams}`)
  }
  const editAdSpotHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    navigate(`../edit/${adSpot?.id}?${searchParams}`)
  }

  return (
    <ModalFullScreen onHide={onCloseModal} loading={loading || modTypeLoading || pinLoading} className={s.modalWrapper}>
      <div></div>
      <div className={s.wrapper}>
        <div className={s.imageWrapper}>
          <img src={adSpot?.preview_url} alt="" className={s.adSpotPreview} />
          <img className={s.metaverseLogo} src={getIconByMetaverse(adSpot?.spot_type?.name)} alt="" />
        </div>
        <div className={s.infoWrapper}>
          <div className={s.row}>
            <div className={s.col}>
              <h3 className={s.header}>{adSpot?.name}</h3>
              <p className={s.info}>
                {adSpot?.spot_type?.name} {(SURFACE_TYPE_BY_VALUE as any)[adSpot?.asset_type || 0]}
              </p>
            </div>
            <div className={`${s.col} ${s.priceAndStatus}`}>
              <Status
                statusClassName={s.status}
                label={STATUS_NAME_BY_STATUS_VALUE[createAdSpotStatus(adSpot?.last_activity)]}
                color={STATUS_STYLE_BY_STATUS_NUMBER[createAdSpotStatus(adSpot?.last_activity)]}
              />
            </div>
          </div>
          {modType?.auto_approval !== undefined && (
            <p className={s.modType}>Moderation type: {modType?.auto_approval ? 'Auto-approval' : 'Manual'}</p>
          )}
          <p className={s.description}>{adSpot?.description}</p>
          <ul className={s.contentList}>
            <li className={s.contentList__item}>
              <div className={s.textTop}>{statistics?.day}</div>
              <div className={s.textBottom}>Shows today</div>
            </li>
            <li className={s.contentList__item}>
              <div className={s.textTop}>{statistics?.interval}</div>
              <Dropmenu
                variant="transparent"
                show={showDropdown}
                title={<span className={s.textBottom}>{`This ${period}`}</span>}
                cref={ref}
                items={[
                  { title: 'This Year', onClick: () => changeIntervalHandler('year') },
                  { title: 'This Month', onClick: () => changeIntervalHandler('month') },
                  { title: 'This Week', onClick: () => changeIntervalHandler('week') },
                  { title: 'This Day', onClick: () => changeIntervalHandler('day') },
                  { title: 'This Hour', onClick: () => changeIntervalHandler('hour') },
                ]}
              />
            </li>
            <li className={s.contentList__item}>
              <div className={s.textTop}>{statistics?.total}</div>
              <div className={s.textBottom}>Total shows</div>
            </li>
          </ul>
          <div className={s.dateAndPin}>
            <div className={s.pin}>
              {location.pathname.includes('my-adspaces') && (
                <>
                  <LockIcon className={`${s.lockIcon} ${adSpot?.is_bound ? s.greenLock : s.redLock}`} />
                  <span>
                    {pin?.pin ? (
                      <>
                        Ad Space PIN: <b>{pin?.pin}</b>
                      </>
                    ) : (
                      <CustomTooltip title={`${pin?.uuid}`}>
                        Ad Space UUID:
                        {!!pin && (
                          <b>{`${pin?.uuid?.slice(0, 3)}...${pin?.uuid?.slice(
                            pin?.uuid?.length - 3,
                            pin?.uuid?.length
                          )}`}</b>
                        )}
                      </CustomTooltip>
                    )}
                  </span>
                </>
              )}
            </div>
            {adSpot?.created_at && (
              <span className={s.date}>
                Created at {format(transformUTCToLocale(adSpot?.created_at), 'dd.MM.yyyy HH:mm')}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={s.buttonsWrapper}>
        {location.pathname.includes('my-adspaces') ? (
          <CustomButton variant="secondary" onClick={editAdSpotHandler} size="240" className={s.editBtn}>
            Edit
          </CustomButton>
        ) : null}
        {adSpot?.jump_url ? (
          <a href={adSpot?.jump_url} target="_blank" rel="noreferrer" className={s.noStyleLink}>
            <CustomButton variant="outlined" size="240" className={s.jumpBtn}>
              Jump To Ad Space
            </CustomButton>
          </a>
        ) : (
          <CustomButton variant="outlined" onClick={onCloseModal} size="152" className={s.closeBtn}>
            Close
          </CustomButton>
        )}
      </div>
    </ModalFullScreen>
  )
})

export default AdSpotInfoModal
